import React from 'react';
import { Link } from 'react-router-dom';
import { FaCloud, FaServer, FaDatabase, FaLock, FaSync, FaChartLine } from 'react-icons/fa';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const AWSServicesPage = () => {
  // SEO data for AWS Services page
  const seoData = {
    title: "AWS Services - Certified AWS Partner Solutions",
    description: `Expert AWS cloud services from certified AWS partner ${contentData.company.name}. AWS migration, infrastructure design, security, DevOps, and cost optimization. Transform your business with proven AWS solutions.`,
    keywords: "AWS Services, AWS Partner, AWS Migration, AWS Infrastructure, AWS Security, AWS DevOps, AWS Cost Optimization, Certified AWS Solutions"
  };

  // AWS services we provide
  const awsServices = [
    {
      icon: <FaCloud className="text-4xl text-primary" />,
      title: "AWS Cloud Migration",
      description: "Seamlessly migrate your existing infrastructure to AWS with our proven migration framework that minimizes downtime and ensures business continuity."
    },
    {
      icon: <FaServer className="text-4xl text-primary" />,
      title: "AWS Infrastructure Design",
      description: "Design scalable, secure, and cost-efficient AWS architectures tailored to your specific business requirements and workloads."
    },
    {
      icon: <FaDatabase className="text-4xl text-primary" />,
      title: "AWS Database Services",
      description: "Implement and manage AWS database solutions including RDS, DynamoDB, Aurora, and Redshift for optimal performance and reliability."
    },
    {
      icon: <FaLock className="text-4xl text-primary" />,
      title: "AWS Security & Compliance",
      description: "Secure your AWS environment with industry best practices and compliance frameworks including HIPAA, PCI-DSS, and GDPR."
    },
    {
      icon: <FaSync className="text-4xl text-primary" />,
      title: "AWS DevOps Implementation",
      description: "Accelerate your development cycles with CI/CD pipelines, infrastructure as code, and automated testing on AWS."
    },
    {
      icon: <FaChartLine className="text-4xl text-primary" />,
      title: "AWS Cost Optimization",
      description: "Reduce your AWS costs through right-sizing, reserved instances, spot instances, and automated resource management."
    }
  ];

  return (
    <>
      <SEO {...seoData} />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl">
              AWS Cloud Solutions from a Certified Partner
            </h1>
            <p className="mb-8 text-xl leading-relaxed text-white/90">
              Leverage our AWS expertise to accelerate your cloud journey, optimize costs, and unlock innovation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="px-8 py-3 font-medium text-primary bg-white rounded-md hover:bg-gray-100">
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="heading">Why Choose an AWS Certified Partner?</h2>
          <p className="subheading max-w-3xl mx-auto">
            Working with an AWS Partner like us gives you access to specialized expertise and resources 
            that accelerate your cloud adoption and maximize your AWS investments.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4">Certified Expertise</h3>
              <p>Our team of AWS-certified professionals brings deep technical knowledge and best practices to every project.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4">AWS Best Practices</h3>
              <p>We implement AWS Well-Architected Framework principles to ensure your cloud environment is secure, reliable, and cost-effective.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold mb-4">Advanced Support</h3>
              <p>As an AWS Partner, we have direct access to AWS resources and support to resolve issues quickly and efficiently.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="heading text-center">Our AWS Services</h2>
          <p className="subheading text-center max-w-3xl mx-auto">
            Comprehensive AWS solutions tailored to meet your business needs
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {awsServices.map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm flex flex-col h-full">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600 flex-grow">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business with AWS?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contact us today to discuss how our AWS expertise can help you achieve your business goals.
          </p>
          <Link to="/contact" className="inline-block px-8 py-3 font-medium text-primary bg-white rounded-md hover:bg-gray-100">
            Get Started Now
          </Link>
        </div>
      </section>
    </>
  );
};

export default AWSServicesPage; 